import moment, { Moment } from 'moment';
import { GB, MB, STRIPE_COUNTRIES } from '../constants/constants';
import { htmlToText } from 'html-to-text';
import { PaypalRecipientStatus } from 'types/src/types';

export function smartExcerpt(text: string, max = 160) {
  if (text.length <= max) return text;
  const slice = text.slice(0, max + 20);
  const lastStop = Math.max(
    slice.lastIndexOf('.'),
    slice.lastIndexOf('!'),
    slice.lastIndexOf('?')
  );
  const cut =
    lastStop >= 60 ? slice.slice(0, lastStop + 1) : text.slice(0, max - 1);
  return cut.replace(/[,\s]+$/, '') + '…';
}

export function htmlDescToText(html = '') {
  return htmlToText(html, {
    wordwrap: false,
    preserveNewlines: false,
    selectors: [
      { selector: 'img', format: 'skip' },
      { selector: 'a', options: { ignoreHref: true } },
      { selector: 'code', format: 'inline' },
    ],
  })
    .replace(/\s+/g, ' ')
    .trim();
}

export function formatStatsNumber(num: number): string | number {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2).replace(/\.0$/, '') + 'mln';
  } else if (num >= 10000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }

  return num;
}

export function getTileEditStatus(content: string, tempValue: string) {
  return {
    wasEdited: tempValue !== content,
    hasContent: !!content?.trim(),
    isEmpty: tempValue.trim() === '',
  };
}

export function convertDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

export function formatCents(value: number) {
  return parseFloat((value / 100).toFixed(2));
}

export function calculateGrowth(current: number, previous: number): number {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return +(((current - previous) / previous) * 100).toFixed(2);
}

export function checkCountryCodeForStripe(countryCode?: string): boolean {
  return STRIPE_COUNTRIES.some((code) => code === countryCode?.toUpperCase());
}

export function shortRelativeTime(input: string | Date | Moment): string {
  const now = moment();

  let target: Moment;

  if (moment.isMoment(input)) {
    target = input;
  } else if (input instanceof Date) {
    target = moment(input);
  } else {
    target = moment(input, moment.ISO_8601, true);
  }

  if (!target.isValid()) {
    return '';
  }

  const diffMinutes = now.diff(target, 'minutes');
  const diffHours = now.diff(target, 'hours');
  const diffDays = now.diff(target, 'days');
  const diffWeeks = now.diff(target, 'weeks');

  if (diffMinutes < 60) return `${diffMinutes} m`;
  if (diffHours < 24) return `${diffHours} h`;
  if (diffDays < 7) return `${diffDays} d`;
  return `${diffWeeks} w`;
}

export function isUserLoggedIn(): boolean {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('isLoggedIn') === 'true';
  }
  return false;
}

export function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }

  const value = n < 10 && i > 0 ? n.toFixed(1) : Math.round(n).toString();
  return `${value} ${units[i]}`;
}

export function isNoFileUploaded(size: string) {
  return size === '0 MB' || size === '0 B';
}

export function parseFilenameFromKey(key: string) {
  const idx = key.lastIndexOf('--');
  return idx >= 0 ? key.slice(idx + 2) : key.split('/').pop() || 'file.zip';
}

export function prettySize(bytes: number) {
  if (bytes >= GB) return `${Math.round(bytes / GB)}GB`;
  if (bytes >= MB) return `${Math.round(bytes / MB)}MB`;
  return `${bytes}B`;
}

type Unit = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

const UNIT_MULTIPLIER: Record<Unit, number> = {
  B: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
  TB: 1024 ** 4,
};

function parseSizeToBytes(input: string): number {
  const s = input.replace(',', '.').trim();
  const m = s.match(/^([\d]+(?:\.\d+)?)\s*([kmgt]?b)$/i);
  if (!m) throw new Error(`Invalid size: "${input}"`);
  const value = parseFloat(m[1]!);
  const unit = (m[2]!.toUpperCase() as Unit) || 'B';
  return value * (UNIT_MULTIPLIER[unit] ?? 1);
}

export function maxSizeString(sizesArray: Array<string | null | undefined>) {
  const items = sizesArray
    .filter(
      (str): str is string => typeof str === 'string' && str.trim().length > 0
    )
    .map((str) => {
      try {
        return { str, bytes: parseSizeToBytes(str) };
      } catch {
        return null;
      }
    })
    .filter((x): x is { str: string; bytes: number } => !!x);

  if (!items.length) return null;
  return items.reduce((best, cur) => (cur.bytes > best.bytes ? cur : best));
}

export function ratingWording(rating: number): string {
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 3.5) return 'Good';
  if (rating >= 2.5) return 'Mixed';
  if (rating >= 1.5) return 'Poor';
  if (rating > 0) return 'Bad';
  return 'Not rated yet';
}

export function getPaypalTooltip(status?: PaypalRecipientStatus) {
  switch (status) {
    case 'active':
      return 'Your PayPal account is connected and ready to receive payouts.';

    case 'email_verified':
      return 'Your email is verified. Your account will become active after the first successful payout.';

    case 'unverified':
      return 'Please verify your PayPal email to receive payouts.';

    case 'blocked':
      return 'Payouts to this PayPal account are currently unavailable. Please contact support.';

    default:
      return 'Connect a PayPal account to receive payouts.';
  }
}
